import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { useGetCheckoutConfirmationQuery } from "../../features/api/checkoutApiSlice";
import { cartApiSlice } from "../../features/api/cartApiSlice";
import { cleanupConfirmedGuestCheckout } from "../../utils/guestCart";

import "./checkoutConfirmation.scss";

// ============================================================
// HELPERS
// ============================================================

const validId = (value) => /^[a-f\d]{24}$/i.test(value || "");

const formatMoney = (amount, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: String(currency).toUpperCase(),
  }).format(Number(amount) || 0);

// ============================================================
// CONFIRMATION CONTENT
// ============================================================

function CheckoutConfirmationContent({ checkoutSessionId }) {
  const dispatch = useDispatch();

  const isValid = validId(checkoutSessionId);

  const [pollCount, setPollCount] = useState(0);

  const lastCleanupSessionRef = useRef(null);
  const lastRegisteredRefreshRef = useRef(null);

  // ============================================================
  // 1. FETCH CONFIRMED CHECKOUT
  // ============================================================

  const { data, error, isLoading, isFetching, refetch } =
    useGetCheckoutConfirmationQuery(checkoutSessionId, {
      skip: !isValid,
      pollingInterval: isValid && pollCount < 20 ? 3000 : 0,
      refetchOnMountOrArgChange: true,
    });

  const order = data?.order;

  const confirmedPaid =
    data?.status === "confirmed" && order?.paymentStatus === "paid";

  const checkoutType = order?.checkoutType;

  // ============================================================
  // 2. CONFIRMATION POLLING LIMIT
  // ============================================================
  //
  // No synchronous setState calls inside the effect.
  // The timer callback updates the polling counter.
  //
  // Polling stops after confirmation or after 20 checks.
  // ============================================================

  useEffect(() => {
    if (!isValid || confirmedPaid || error || pollCount >= 20) {
      return;
    }

    const timeout = setTimeout(() => {
      setPollCount((count) => count + 1);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [isValid, confirmedPaid, error, pollCount]);

  // ============================================================
  // 3. GUEST CART CLEANUP — STEP 112B
  // ============================================================
  //
  // Only run after backend verification of a paid order.
  //
  // Guest checkout:
  // - Uses the saved localStorage checkout snapshot.
  // - Validates the purchased items.
  // - Removes matching purchased cart lines.
  // - Preserves unrelated or changed items.
  // - Prevents duplicate cleanup.
  //
  // Registered checkout never runs guest cleanup.
  // ============================================================

  useEffect(() => {
    if (
      !isValid ||
      !confirmedPaid ||
      checkoutType !== "guest" ||
      lastCleanupSessionRef.current === checkoutSessionId
    ) {
      return;
    }

    lastCleanupSessionRef.current = checkoutSessionId;

    try {
      const result = cleanupConfirmedGuestCheckout({
        checkoutSessionId,
        order,
      });

      if (result.cleaned) {
        console.log("Guest checkout cart cleanup completed:", result);
      } else {
        console.log("Guest checkout cart cleanup status:", result.reason);
      }
    } catch (cleanupError) {
      console.error("Guest cart cleanup failed:", cleanupError);
    }
  }, [isValid, confirmedPaid, checkoutType, checkoutSessionId, order]);

  // ============================================================
  // 4. REGISTERED CART REFRESH — STEP 112C
  // ============================================================
  //
  // Backend responsibilities:
  // - Verify Stripe payment.
  // - Create the paid order.
  // - Remove matching purchased items from MongoDB.
  // - Commit all changes in the same transaction.
  //
  // Frontend responsibility:
  // - Invalidate the RTK Query Cart cache.
  // - Allow subscribed components to refetch the cart.
  //
  // This effect does not delete any database records.
  // ============================================================

  useEffect(() => {
    if (
      !isValid ||
      !confirmedPaid ||
      checkoutType !== "registered" ||
      lastRegisteredRefreshRef.current === checkoutSessionId
    ) {
      return;
    }

    lastRegisteredRefreshRef.current = checkoutSessionId;

    dispatch(cartApiSlice.util.invalidateTags(["Cart"]));

    console.log(
      "Registered checkout confirmed. Cart cache invalidated:",
      checkoutSessionId
    );
  }, [dispatch, isValid, confirmedPaid, checkoutType, checkoutSessionId]);

  // ============================================================
  // 5. VIEW STATUS
  // ============================================================

  const processing = isValid && !error && !confirmedPaid && pollCount < 20;

  const accessDenied = error?.status === 401 || error?.status === 403;

  // ============================================================
  // 6. RENDER
  // ============================================================

  return (
    <main className="checkout-confirmation">
      <div className="checkout-confirmation__shell">
        {/* INVALID CHECKOUT REFERENCE */}

        {!isValid ? (
          <section className="checkout-confirmation__panel">
            <h1>Checkout reference missing</h1>

            <p>
              We couldn't identify the checkout to display. Check the link you
              used or return to the shop.
            </p>

            <Link to="/shop" className="checkout-confirmation__button">
              Return to shop
            </Link>
          </section>
        ) : accessDenied ? (
          /* CONFIRMATION ACCESS DENIED */

          <section className="checkout-confirmation__panel">
            <h1>Confirmation access unavailable</h1>

            <p>
              Your confirmation link may have expired, or you're viewing it in
              another browser. You can request a secure recovery email using the
              email address from your order.
            </p>

            <Link
              className="checkout-confirmation__button"
              to={`/checkout/recover?checkoutSessionId=${encodeURIComponent(
                checkoutSessionId
              )}`}
            >
              Recover my order
            </Link>
          </section>
        ) : error ? (
          /* CONFIRMATION REQUEST ERROR */

          <section className="checkout-confirmation__panel">
            <h1>We couldn't load your order</h1>

            <p>
              This doesn't mean your payment failed. Please try again in a
              moment.
            </p>

            <button
              type="button"
              className="checkout-confirmation__button"
              onClick={() => refetch()}
            >
              Try again
            </button>
          </section>
        ) : confirmedPaid ? (
          /* PAID ORDER CONFIRMATION */

          <section className="checkout-confirmation__panel">
            <p className="checkout-confirmation__eyebrow">
              MINI MILES ADVENTURES · GEAR SHOP
            </p>

            <h1>Thank you for your order!</h1>

            <p>
              Your order is confirmed. A receipt will be sent to the email
              address used at checkout.
            </p>

            {/* ORDER METADATA */}

            <div className="checkout-confirmation__meta">
              <div>
                <span>Order number</span>
                <strong>{order.orderNumber}</strong>
              </div>

              <div>
                <span>Payment status</span>
                <strong>{order.paymentStatus}</strong>
              </div>

              {order.createdAt && (
                <div>
                  <span>Order date</span>
                  <strong>
                    {new Date(order.createdAt).toLocaleDateString()}
                  </strong>
                </div>
              )}
            </div>

            {/* ORDER ITEMS */}

            <h2>Order summary</h2>

            <div className="checkout-confirmation__items">
              {(order.items || []).map((item, index) => (
                <div
                  className="checkout-confirmation__item"
                  key={`${item.productId || index}-${
                    item.variantId || index
                  }-${index}`}
                >
                  {item.image ? (
                    <img src={item.image} alt="" loading="lazy" />
                  ) : (
                    <div
                      className="checkout-confirmation__image-placeholder"
                      aria-hidden="true"
                    />
                  )}

                  <div className="checkout-confirmation__item-details">
                    <strong>{item.name}</strong>

                    {(item.size || item.color || item.volume) && (
                      <span>
                        {[item.size, item.color, item.volume]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    )}

                    <span>Quantity: {item.quantity}</span>
                  </div>

                  <strong>
                    {formatMoney(
                      (Number(item.price) || 0) * (Number(item.quantity) || 0),
                      order.financials?.currency
                    )}
                  </strong>
                </div>
              ))}
            </div>

            {/* ORDER TOTALS */}

            <div className="checkout-confirmation__totals">
              <div>
                <span>Subtotal</span>

                <span>
                  {formatMoney(
                    order.financials?.subtotal,
                    order.financials?.currency
                  )}
                </span>
              </div>

              <div>
                <span>Discount</span>

                <span>
                  −
                  {formatMoney(
                    order.financials?.discount,
                    order.financials?.currency
                  )}
                </span>
              </div>

              <div>
                <span>Shipping</span>

                <span>
                  {formatMoney(
                    order.financials?.shipping,
                    order.financials?.currency
                  )}
                </span>
              </div>

              <div>
                <span>Tax</span>

                <span>
                  {formatMoney(
                    order.financials?.tax,
                    order.financials?.currency
                  )}
                </span>
              </div>

              <div className="checkout-confirmation__total">
                <strong>Total paid</strong>

                <strong>
                  {formatMoney(
                    order.financials?.total,
                    order.financials?.currency
                  )}
                </strong>
              </div>
            </div>

            {/* CONTINUE SHOPPING */}

            <Link to="/shop" className="checkout-confirmation__button">
              Continue shopping
            </Link>
          </section>
        ) : (
          /* PAYMENT PROCESSING */

          <section
            className="checkout-confirmation__panel"
            role="status"
            aria-live="polite"
          >
            <p className="checkout-confirmation__eyebrow">
              MINI MILES ADVENTURES · GEAR SHOP
            </p>

            <h1>
              {processing
                ? "Finalizing your order…"
                : "Your order is taking longer than expected"}
            </h1>

            <p>
              {processing
                ? "We're waiting for secure payment confirmation and your order record. Please don't submit another payment."
                : "Your payment may still be processing. You can retry checking without placing another order."}
            </p>

            {(isLoading || isFetching) && (
              <p className="checkout-confirmation__muted">
                Checking your order status…
              </p>
            )}

            {!processing && (
              <button
                type="button"
                className="checkout-confirmation__button"
                onClick={() => {
                  setPollCount(0);
                  refetch();
                }}
              >
                Check again
              </button>
            )}
          </section>
        )}
      </div>
    </main>
  );
}

// ============================================================
// PAGE WRAPPER
// ============================================================
//
// Keying the content by checkoutSessionId ensures React
// creates a fresh component when navigating to a different
// checkout session.
//
// This resets polling and cleanup refs without calling
// setState synchronously inside an effect.
// ============================================================

export default function CheckoutConfirmation() {
  const [searchParams] = useSearchParams();

  const checkoutSessionId = searchParams.get("checkoutSessionId") || "";

  return (
    <CheckoutConfirmationContent
      key={checkoutSessionId}
      checkoutSessionId={checkoutSessionId}
    />
  );
}
